import React, { useState, useEffect } from "react";
import { useController, useFormContext } from "react-hook-form";
import {
  CircularProgress,
  MenuItem,
  Paper,
  ClickAwayListener,
  Stack,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import { useDebounce, useGetGoogleAddressList } from "../../hooks";
import { StyledFormControl } from "./styles";
import { InputLabel } from "../InputLabel";
import { HubIcon } from "../HubIcon";
import { StyledPopper } from "./styles";
import HubContextMenuItem from "../HubContextMenu/HubContextMenuItem";
import SearchResultsEmpty from "../NavBars/TopNavBar/SearchResultsEmpty";

export const HubHookAddressInput = ({
  name = "address",
  label = "Address",
  placeholder = "Search for an address...",
  required = false,
  disabled = false,
}) => {
  const { setValue } = useFormContext();
  const {
    field: { onChange, value, ref },
    fieldState: { error },
  } = useController({ name, rules: { required }, defaultValue: "" });

  const [inputValue, setInputValue] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [inputFromSelect, setInputFromSelect] = useState(false);

  const { data, isFetching, refetch } = useGetGoogleAddressList(inputValue);

  const handleRefetch = () => {
    if (!inputFromSelect) {
      refetch();
      setOpen(true);
    }
  };

  const debouncedSearchTerm = useDebounce(handleRefetch, 500);

  // Fetch addresses from Nominatim
  useEffect(() => {
    if (inputValue?.length > 2 && !inputFromSelect)
      debouncedSearchTerm(inputValue);
    else setOpen(false);
  }, [inputValue]);

  // Handle address selection
  const handleSelect = (address) => {
    setInputFromSelect(true);
    setInputValue(address);
    setValue(name, address);
    setOpen(false);
  };

  // Handle input change
  const handleInputChange = (event) => {
    setInputFromSelect(false);
    setInputValue(event.target.value);
    onChange(event.target.value);
    setAnchorEl(event.target.offsetParent);
  };

  return (
    <StyledFormControl fullWidth variant="outlined">
      <InputLabel label={label} required={required} />
      <OutlinedInput
        inputRef={ref}
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        size="small"
        error={!!error}
        startAdornment={
          <InputAdornment position="start">
            <HubIcon icon="MapPin" size={12} />
          </InputAdornment>
        }
        endAdornment={
          isFetching && (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          )
        }
      />
      <StyledPopper open={open} anchorEl={anchorEl} placement="bottom-start">
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper style={{ width: anchorEl?.clientWidth || "100%" }}>
            <Stack>
              {data?.length ? (
                data?.map(({ placePrediction }) => (
                  <HubContextMenuItem
                    key={placePrediction.text.text}
                    item={{ label: placePrediction.text.text, icon: "MapPin" }}
                    onSelect={() => handleSelect(placePrediction.text.text)}
                    sx={{ ".MuiTypography-root": { fontSize: 13 } }}
                  />
                ))
              ) : (
                <SearchResultsEmpty />
              )}
            </Stack>
          </Paper>
        </ClickAwayListener>
      </StyledPopper>
    </StyledFormControl>
  );
};

export default HubHookAddressInput;
